/**
 * LLM Profiles — Centrální registr LLM modelů a proxy
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { AsyncStatusToggle } from '../../components/ui/AsyncStatusToggle';
import { EmptyState } from '../../components/ui/EmptyState';
import { InlineActions } from '../../components/ui/InlineActions';
import {
  Bot,
  Cpu,
  Download,
  Globe,
  KeyRound,
  Layers,
  Lock,
  Plus,
  Server,
  Tag,
  User,
} from 'lucide-react';
import { themedConfirm, themedPrompt } from '../../lib/themed-dialogs';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Template {
  id: string;
  label: string;
  url: string;
  model: string;
  backend: string;
  description: string;
  tags: string[];
  ctx_max: number;
  admin_only: boolean;
  /** Lze publikovat ven přes proxy? true (default) | false = internal only */
  publishable?: boolean;
  created_at: string;
  updated_at: string;
}

interface Profile {
  id: string;
  template_id: string | null;
  label: string | null;
  url: string | null;
  model: string | null;
  backend: string | null;
  api_key: string;
  enabled: boolean;
  priority: number;
  daily_token_limit: number;
  admin_only: boolean;
  created_at?: string;
  updated_at?: string;
  /** 'global' = sdílený napříč meshem; '<node_id>' = jen na tomto nodu */
  node_scope?: string;
  /** Seznam konzumentů — kteří moduly se proxy nabízí. Empty/missing = všem. */
  consumers?: string[];
  codable_compatible?: boolean;
  codable_enabled?: boolean;
  agent_family?: 'codex' | 'claude' | 'openai' | 'qwen' | 'gpt-oss' | 'generic';
  preferred_patch_mode?: 'full-file' | 'unified-diff';
  context_budget?: number;
  vision?: boolean;
  timeout_ms?: number;
  health_status?: 'ok' | 'unreachable' | 'error' | 'unknown' | string;
  last_health_check?: string | null;
  health_consecutive_failures?: number;
  reachable_from?: string[] | unknown;
}

interface ExternalKey {
  id: string;
  label: string;
  key_prefix: string;
  allowed_models: string[];
  allowed_proxies: string[] | null;
  rate_limit_rpm: number | null;
  concurrent_limit: number | null;
  entity_id: string | null;
  in_flight?: number;
  created_by: string;
  issued_by: string | null;
  issued_by_label: string | null;
  issued_by_external_id: string | null;
  issuance_source: string;
  created_at: string;
  expires_at: string | null;
  last_used: string | null;
  last_used_model: string | null;
  enabled: boolean;
}

interface EntityChoice {
  id: string;
  label: string;
  type: string;
}

interface NetworkEntry {
  key: string;
  value: {
    url: string;
    service: string;
    instance_ref?: string;
    desired: boolean;
    actual: boolean;
    cert_id?: number;
    extras?: Record<string, unknown>;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtCtx(n: number) {
  if (!n) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}

function BackendBadge({ backend }: { backend: string }) {
  if (backend === 'anthropic') {
    return <Badge variant='purple'>anthropic</Badge>;
  }
  return <Badge variant='blue'>openai</Badge>;
}

// ─── SimpleTabs ───────────────────────────────────────────────────────────────

function SimpleTabs({
  tabs,
  active,
  onSwitch,
}: {
  tabs: { id: string; label: string }[];
  active: string;
  onSwitch: (id: string) => void;
}) {
  return (
    <div className='flex gap-1 border-b border-gray-800 mb-6'>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onSwitch(t.id)}
          className={[
            'px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors',
            active === t.id
              ? 'border-blue-500 text-blue-400 bg-blue-500/5'
              : 'border-transparent text-gray-500 hover:text-gray-300',
          ].join(' ')}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── TemplateForm ─────────────────────────────────────────────────────────────

type TplFormData = {
  id: string;
  label: string;
  url: string;
  model: string;
  backend: string;
  description: string;
  tags: string[];
  ctx_max: number;
  admin_only: boolean;
  publishable: boolean;
};

const TPL_DEFAULTS: TplFormData = {
  id: '',
  label: '',
  url: '',
  model: '',
  backend: 'openai',
  description: '',
  tags: [],
  ctx_max: 0,
  admin_only: false,
  publishable: true,
};

function TemplateForm({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: TplFormData | null;
  onClose: () => void;
  onSave: (data: TplFormData) => Promise<void>;
}) {
  const { t } = useTranslation();
  const isEdit = !!initial?.id;
  const [f, setF] = useState<TplFormData>(initial ?? TPL_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [tagsStr, setTagsStr] = useState((initial?.tags ?? []).join(', '));

  // Synchronizace při každém otevření
  if (open && !saving) {
    // Používáme klíč (key prop na Modal) pro reset
  }

  const set = <K extends keyof TplFormData>(k: K, v: TplFormData[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!f.id || !f.label || !f.model) {
      toast.error(t('llmProfiles.requiredTemplateFields'));
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...f, tags: tagsStr.split(',').map((s) => s.trim()).filter(Boolean) });
      onClose();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t('llmProfiles.editModel') : t('llmProfiles.newModel')}
      size='lg'
      footer={
        <>
          <Button variant='secondary' onClick={onClose}>{t('llmProfiles.cancel')}</Button>
          <Button variant='primary' onClick={submit} disabled={saving}>
            {saving ? t('llmProfiles.saving') : t('llmProfiles.save')}
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              ID <span className='text-red-400'>*</span>
            </label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500'
              value={f.id}
              onChange={(e) => set('id', e.target.value)}
              placeholder='gb10-qwen3-coder'
              disabled={isEdit}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              {t('llmProfiles.name')} <span className='text-red-400'>*</span>
            </label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500'
              value={f.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder='GB10 — Qwen3-Coder'
            />
          </div>
        </div>

        <div>
          <label className='block text-xs text-gray-400 mb-1'>{t('llmProfiles.urlEndpoint')}</label>
          <input
            className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500'
            value={f.url}
            onChange={(e) => set('url', e.target.value)}
            placeholder='https://10.118.200.18:8004/v1'
          />
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              Model <span className='text-red-400'>*</span>
            </label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500'
              value={f.model}
              onChange={(e) => set('model', e.target.value)}
              placeholder='qwen3-coder-30b'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>{t('llmProfiles.backend')}</label>
            <Select
              className='w-full'
              value={f.backend}
              onChange={(e) => set('backend', e.target.value)}
            >
              <option value='openai'>openai (OpenAI-compatible)</option>
              <option value='anthropic'>anthropic (native)</option>
            </Select>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              {t('llmProfiles.maxContextTokens')}
            </label>
            <input
              type='number'
              min={0}
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
              value={f.ctx_max}
              onChange={(e) => set('ctx_max', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>{t('llmProfiles.tagsComma')}</label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500'
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
              placeholder='local, coding, fast'
            />
          </div>
        </div>

        <div>
          <label className='block text-xs text-gray-400 mb-1'>{t('llmProfiles.description')}</label>
          <textarea
            rows={2}
            className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none'
            value={f.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder={t('llmProfiles.descriptionPlaceholder')}
          />
        </div>

        <label className='flex items-center gap-3 cursor-pointer select-none'>
          <input
            type='checkbox'
            className='w-4 h-4 accent-blue-500'
            checked={f.admin_only}
            onChange={(e) => set('admin_only', e.target.checked)}
          />
          <span className='text-sm text-gray-300 flex items-center gap-1'>
            <Lock size={13} className='opacity-60' />
            {t('llmProfiles.adminOnlyLabel')}
          </span>
        </label>

        <label className='flex items-center gap-3 cursor-pointer select-none'>
          <input
            type='checkbox'
            className='w-4 h-4 accent-blue-500'
            checked={f.publishable !== false}
            onChange={(e) => set('publishable', e.target.checked)}
          />
          <span className='text-sm text-gray-300 flex items-center gap-1'>
            <Globe size={13} className='opacity-60' />
            {t('llmProfiles.publishableLabel')}
            <span className='text-xs text-gray-500'>{t('llmProfiles.publishableHint')}</span>
          </span>
        </label>
      </div>
    </Modal>
  );
}

// ─── ProfileForm ──────────────────────────────────────────────────────────────

type ProfFormData = {
  id: string;
  template_id: string | null;
  label: string | null;
  url: string | null;
  model: string | null;
  backend: string | null;
  api_key: string;
  enabled: boolean;
  priority: number;
  daily_token_limit: number;
  admin_only: boolean;
  /** Seznam konzumentů — kteří moduly se proxy nabízí. Empty = všem. */
  consumers: string[];
  codable_compatible: boolean;
  codable_enabled: boolean;
  agent_family: '' | 'codex' | 'claude' | 'openai' | 'qwen' | 'gpt-oss' | 'generic';
  preferred_patch_mode: 'full-file' | 'unified-diff';
  context_budget: number;
  vision: boolean;
  timeout_ms: number;
};

/** Známí konzumenti — nový se může přidat ručně přes API. */
const KNOWN_CONSUMERS = [
  'chat',
  'codable',
  'superagent',
  'localcode',
  'ocr',
  'webui',
  'gateway',
  'darwinfw',
] as const;

const PROF_DEFAULTS: ProfFormData = {
  id: '',
  template_id: null,
  label: null,
  url: null,
  model: null,
  backend: null,
  api_key: '',
  enabled: true,
  priority: 0,
  daily_token_limit: 0,
  admin_only: false,
  consumers: [],
  codable_compatible: false,
  codable_enabled: false,
  agent_family: '',
  preferred_patch_mode: 'full-file',
  context_budget: 120000,
  vision: false,
  timeout_ms: 120000,
};

function ProfileForm({
  open,
  initial,
  templates,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: ProfFormData | null;
  templates: Template[];
  onClose: () => void;
  onSave: (data: ProfFormData) => Promise<void>;
}) {
  const { t } = useTranslation();
  const isEdit = !!initial?.id;
  const [f, setF] = useState<ProfFormData>(initial ?? PROF_DEFAULTS);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof ProfFormData>(k: K, v: ProfFormData[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  const n = (v: string | null) => v ?? '';

  const submit = async () => {
    if (!f.id) {
      toast.error(t('llmProfiles.requiredFieldId'));
      return;
    }
    setSaving(true);
    try {
      await onSave(f);
      onClose();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t('llmProfiles.editProxy') : t('llmProfiles.newProxy')}
      size='lg'
      footer={
        <>
          <Button variant='secondary' onClick={onClose}>{t('llmProfiles.cancel')}</Button>
          <Button variant='primary' onClick={submit} disabled={saving}>
            {saving ? t('llmProfiles.saving') : t('llmProfiles.save')}
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              ID <span className='text-red-400'>*</span>
            </label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500'
              value={f.id}
              onChange={(e) => set('id', e.target.value)}
              placeholder='muj-openai-profil'
              disabled={isEdit}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              {t('llmProfiles.modelOptional')}
            </label>
            <Select
              className='w-full'
              value={n(f.template_id)}
              onChange={(e) => set('template_id', e.target.value || null)}
            >
              <option value=''>{t('llmProfiles.noModel')}</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </Select>
          </div>
        </div>

        <div className='bg-gray-800/40 border border-gray-700/50 rounded-lg p-3'>
          <p className='text-xs text-gray-500 mb-3'>
            {t('llmProfiles.overrideHint')}
          </p>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>
                {t('llmProfiles.nameOverride')}
              </label>
              <input
                className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500'
                value={n(f.label)}
                onChange={(e) => set('label', e.target.value || null)}
                placeholder={t('llmProfiles.fromModelPlaceholder')}
              />
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>
                {t('llmProfiles.modelOverride')}
              </label>
              <input
                className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500'
                value={n(f.model)}
                onChange={(e) => set('model', e.target.value || null)}
                placeholder={t('llmProfiles.fromModelPlaceholder')}
              />
            </div>
          </div>
          <div className='mt-3'>
            <label className='block text-xs text-gray-400 mb-1'>
              {t('llmProfiles.urlOverride')}
            </label>
            <input
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500'
              value={n(f.url)}
              onChange={(e) => set('url', e.target.value || null)}
              placeholder={t('llmProfiles.fromModelPlaceholder')}
            />
          </div>
        </div>

        <div>
          <label className='block text-xs text-gray-400 mb-1 flex items-center gap-1'>
            <KeyRound size={11} /> {t('llmProfiles.apiKey')}
          </label>
          <input
            type='password'
            className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono'
            value={f.api_key}
            onChange={(e) => set('api_key', e.target.value)}
            placeholder={isEdit ? t('llmProfiles.apiKeyKeepPlaceholder') : 'sk-…'}
            autoComplete='new-password'
          />
        </div>

        <div className='grid grid-cols-3 gap-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>{t('llmProfiles.priority')}</label>
            <input
              type='number'
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
              value={f.priority}
              onChange={(e) => set('priority', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>
              {t('llmProfiles.dailyTokenLimit')}
            </label>
            <input
              type='number'
              min={0}
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
              value={f.daily_token_limit}
              onChange={(e) => set('daily_token_limit', parseInt(e.target.value) || 0)}
            />
          </div>
          <div className='flex flex-col gap-2 justify-end pb-2'>
            <label className='flex items-center gap-2 cursor-pointer'>
              <input
                type='checkbox'
                className='w-4 h-4 accent-blue-500'
                checked={f.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
              />
              <span className='text-sm text-gray-300'>{t('llmProfiles.active')}</span>
            </label>
            <label className='flex items-center gap-2 cursor-pointer'>
              <input
                type='checkbox'
                className='w-4 h-4 accent-blue-500'
                checked={f.admin_only}
                onChange={(e) => set('admin_only', e.target.checked)}
              />
              <span className='text-sm text-gray-300 flex items-center gap-1'>
                <Lock size={11} />Admin only
              </span>
            </label>
          </div>
        </div>

        <div className='mt-3 p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg'>
          <div className='text-xs text-gray-300 mb-2 flex items-center gap-1'>
            <Layers size={12} />
            {t('llmProfiles.availableForModules')}
            <span className='text-gray-600'>
              ({f.consumers.length === 0
                ? t('llmProfiles.allModules')
                : t('llmProfiles.selectedCount', { value: f.consumers.length })})
            </span>
          </div>
          <div className='flex flex-wrap gap-1.5'>
            {KNOWN_CONSUMERS.map((c) => {
              const active = f.consumers.includes(c);
              return (
                <button
                  key={c}
                  type='button'
                  onClick={() =>
                    set(
                      'consumers',
                      active ? f.consumers.filter((x) => x !== c) : [...f.consumers, c],
                    )}
                  className={`px-2 py-0.5 text-xs rounded-lg border transition-colors ${
                    active
                      ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                      : 'bg-gray-900 border-gray-700 text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          <div className='text-xs text-gray-500 mt-2'>
            {t('llmProfiles.consumersHint')} <code className='text-gray-400'>?consumer=name</code>).
          </div>
        </div>

        <div className='mt-3 p-3 bg-gray-800/40 border border-gray-700/50 rounded-lg'>
          <div className='text-xs text-gray-300 mb-2 flex items-center gap-1'>
            <Bot size={12} />
            {t('llmProfiles.codableSection', 'Codable')}
          </div>
          <div className='grid grid-cols-2 gap-3'>
            <label className='flex items-center gap-2 cursor-pointer'>
              <input
                type='checkbox'
                className='w-4 h-4 accent-blue-500'
                checked={f.codable_compatible}
                onChange={(e) => set('codable_compatible', e.target.checked)}
              />
              <span className='text-sm text-gray-300'>
                {t('llmProfiles.codableCompatible')}
              </span>
            </label>
            <label className='flex items-center gap-2 cursor-pointer'>
              <input
                type='checkbox'
                className='w-4 h-4 accent-blue-500'
                checked={f.codable_enabled}
                onChange={(e) => {
                  set('codable_enabled', e.target.checked);
                  if (e.target.checked && !f.consumers.includes('codable')) {
                    set('consumers', [...f.consumers, 'codable']);
                  }
                }}
              />
              <span className='text-sm text-gray-300'>
                {t('llmProfiles.codableEnabled', 'Povolit v Codable pickeru')}
              </span>
            </label>
          </div>
          <div className='grid grid-cols-2 gap-3 mt-3'>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>
                {t('llmProfiles.agentFamily')}
              </label>
              <Select
                className='w-full'
                value={f.agent_family}
                onChange={(e) =>
                  set('agent_family', e.target.value as ProfFormData['agent_family'])}
              >
                <option value=''>{t('llmProfiles.autoDetect', 'Auto')}</option>
                <option value='codex'>codex</option>
                <option value='claude'>claude</option>
                <option value='openai'>openai</option>
                <option value='qwen'>qwen</option>
                <option value='gpt-oss'>gpt-oss</option>
                <option value='generic'>generic</option>
              </Select>
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>
                {t('llmProfiles.patchMode', 'Patch preference')}
              </label>
              <Select
                className='w-full'
                value={f.preferred_patch_mode}
                onChange={(e) =>
                  set(
                    'preferred_patch_mode',
                    e.target.value as ProfFormData['preferred_patch_mode'],
                  )}
              >
                <option value='full-file'>full-file</option>
                <option value='unified-diff'>unified-diff</option>
              </Select>
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>
                {t('llmProfiles.contextBudget', 'Context budget')}
              </label>
              <input
                type='number'
                min={0}
                className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
                value={f.context_budget}
                onChange={(e) => set('context_budget', parseInt(e.target.value, 10) || 0)}
              />
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>
                {t('llmProfiles.timeoutMs', 'Timeout ms')}
              </label>
              <input
                type='number'
                min={0}
                className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
                value={f.timeout_ms}
                onChange={(e) => set('timeout_ms', parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </div>
          <label className='flex items-center gap-2 cursor-pointer mt-3'>
            <input
              type='checkbox'
              className='w-4 h-4 accent-blue-500'
              checked={f.vision}
              onChange={(e) => set('vision', e.target.checked)}
            />
            <span className='text-sm text-gray-300'>
              {t('llmProfiles.visionInput')}
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
}

// ─── ImportFromUrlModal ───────────────────────────────────────────────────────

function ImportFromUrlModal({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    id: '',
    label: '',
    url: '',
    model: '',
    backend: 'openai' as 'openai' | 'anthropic',
    bearer: '',
    description: '',
    ctx_max: 32768,
    skip_probe: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.id.trim() || !form.label.trim() || !form.url.trim() || !form.model.trim()) {
      toast.error(t('llmProfiles.requiredImportFields'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.mcp<{ ok?: boolean; error?: string; note?: string }>(
        'llm-profiles.llm_model_import_from_url',
        form as unknown as Record<string, unknown>,
      );
      if (res.error) {
        toast.error(res.error);
      } else {
        toast.success(t('llmProfiles.modelImported', { id: form.id }));
        if (res.note) toast.info(res.note);
        onImported();
        onClose();
        setForm({ ...form, id: '', label: '', url: '', model: '', bearer: '' });
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('llmProfiles.importModalTitle')}>
      <div className='space-y-3'>
        <p className='text-xs text-gray-500'>
          {t('llmProfiles.importIntro')}
        </p>

        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>{t('llmProfiles.idSlug')}</label>
            <input
              type='text'
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
              placeholder='federated-acm-b-qwen36'
              className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>
              {t('llmProfiles.labelRequired')}
            </label>
            <input
              type='text'
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder='ACM-B Qwen3.6 (federation)'
              className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
            />
          </div>
        </div>

        <div>
          <label className='block text-xs text-gray-500 mb-1'>
            {t('llmProfiles.baseUrlRequired')}
          </label>
          <input
            type='text'
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder={t('llmProfiles.baseUrlPlaceholder')}
            className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
          />
        </div>

        <div className='grid grid-cols-3 gap-3'>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>
              {t('llmProfiles.modelIdRequired')}
            </label>
            <input
              type='text'
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              placeholder='qwen36-35b-vl'
              className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>{t('llmProfiles.backend')}</label>
            <Select
              value={form.backend}
              onChange={(e) =>
                setForm({ ...form, backend: e.target.value as 'openai' | 'anthropic' })}
              className='w-full'
            >
              <option value='openai'>OpenAI-compatible</option>
              <option value='anthropic'>Anthropic</option>
            </Select>
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>{t('llmProfiles.ctxMax')}</label>
            <input
              type='number'
              value={form.ctx_max}
              onChange={(e) => setForm({ ...form, ctx_max: Number(e.target.value) })}
              className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
            />
          </div>
        </div>

        <div>
          <label className='block text-xs text-gray-500 mb-1'>{t('llmProfiles.bearerToken')}</label>
          <input
            type='password'
            value={form.bearer}
            onChange={(e) => setForm({ ...form, bearer: e.target.value })}
            placeholder={t('llmprofiles.skAcmNeboSk', 'sk-acm-... nebo sk-...')}
            className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
          />
        </div>

        <label className='flex items-center gap-2 text-xs text-gray-500'>
          <input
            type='checkbox'
            className='accent-blue-500'
            checked={form.skip_probe}
            onChange={(e) => setForm({ ...form, skip_probe: e.target.checked })}
          />
          {t('llmProfiles.skipProbe')}
        </label>

        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='ghost' onClick={onClose}>{t('llmProfiles.cancel')}</Button>
          <Button variant='primary' onClick={submit} disabled={submitting}>
            {submitting ? t('llmProfiles.importing') : t('llmProfiles.import')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── TemplatesTab ─────────────────────────────────────────────────────────────

function TemplatesTab({ templates }: { templates: Template[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<Template | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [importOpen, setImportOpen] = useState(false);

  const save = useMutation({
    mutationFn: async (data: TplFormData) => {
      const res = await api.mcp<{ ok?: boolean; error?: string }>(
        'llm-profiles.template_upsert',
        data as unknown as Record<string, unknown>,
      );
      if (res.error) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      toast.success(t('llmProfiles.modelSaved'));
      qc.invalidateQueries({ queryKey: ['llm-templates'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.mcp<{ ok?: boolean; error?: string }>(
        'llm-profiles.template_delete',
        { id },
      );
      if (res.error) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      toast.success(t('llmProfiles.modelDeleted'));
      qc.invalidateQueries({ queryKey: ['llm-templates'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const doExport = async () => {
    try {
      const data = await api.mcp<{
        version: number;
        exported_at: string;
        templates: Template[];
        error?: string;
      }>('llm-profiles.export', {});
      if (data.error) throw new Error(data.error);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `llm-templates-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('llmProfiles.exportDownloaded'));
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const openEdit = (t: Template) => {
    setEditItem(t);
    setFormKey((k) => k + 1);
    setModalOpen(true);
  };

  const openNew = () => {
    setEditItem(null);
    setFormKey((k) => k + 1);
    setModalOpen(true);
  };

  if (!templates.length) {
    return (
      <EmptyState
        icon={<Layers size={32} className='text-gray-600' />}
        title={t('llmProfiles.noModelsTitle')}
        description={t('llmProfiles.noModelsDesc')}
        action={
          <Button variant='primary' onClick={openNew}>
            <Plus size={14} className='mr-1' />
            {t('llmProfiles.newModel')}
          </Button>
        }
      />
    );
  }

  return (
    <>
      <div className='flex items-center justify-between mb-4'>
        <span className='text-sm text-gray-500'>
          {t('llmProfiles.modelsCount', { value: templates.length })}
        </span>
        <div className='flex gap-2'>
          <Button variant='secondary' onClick={doExport}>
            <Download size={14} className='mr-1' />
            {t('llmProfiles.exportJson')}
          </Button>
          <Button variant='secondary' onClick={() => setImportOpen(true)}>
            <Globe size={14} className='mr-1' />
            {t('llmProfiles.importFromUrl')}
          </Button>
          <Button variant='primary' onClick={openNew}>
            <Plus size={14} className='mr-1' />
            {t('llmProfiles.newModel')}
          </Button>
        </div>
      </div>

      <ImportFromUrlModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => qc.invalidateQueries({ queryKey: ['llm-templates'] })}
      />

      <div className='space-y-2'>
        {templates.map((tpl) => (
          <Card key={tpl.id} className='group'>
            <CardContent className='p-0'>
              <div className='flex items-start gap-4 px-4 py-3'>
                <div className='mt-0.5 text-gray-500 flex-shrink-0'>
                  {tpl.tags.includes('local') ? <Server size={16} /> : <Globe size={16} />}
                </div>

                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='font-medium text-sm text-white'>{tpl.label}</span>
                    <BackendBadge backend={tpl.backend} />
                    {tpl.admin_only && (
                      <Badge variant='red'>
                        <Lock size={10} className='mr-0.5 inline' />admin
                      </Badge>
                    )}
                    {tpl.tags.map((tag) => (
                      <Badge key={tag} variant='gray'>
                        <Tag size={9} className='mr-0.5 inline' />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <div className='text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3'>
                    <span className='font-mono'>{tpl.model}</span>
                    {tpl.url && <span className='opacity-60 truncate max-w-xs'>@ {tpl.url}</span>}
                    {tpl.ctx_max > 0 && <span>ctx {fmtCtx(tpl.ctx_max)}</span>}
                  </div>
                  {tpl.description && (
                    <p className='text-xs text-gray-600 mt-0.5 truncate'>{tpl.description}</p>
                  )}
                </div>

                <div className='flex items-center gap-3 flex-shrink-0'>
                  <span className='text-xs text-gray-600 hidden sm:block'>
                    {fmtDate(tpl.created_at)}
                  </span>
                  <InlineActions.Row
                    onEdit={(e) => {
                      e.stopPropagation();
                      openEdit(tpl);
                    }}
                    onDelete={() => del.mutate(tpl.id)}
                    confirmDelete={t('llmProfiles.confirmDeleteModel', { name: tpl.label })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <TemplateForm
        key={formKey}
        open={modalOpen}
        initial={editItem ? { ...editItem } : null}
        onClose={() => setModalOpen(false)}
        onSave={(data) => save.mutateAsync(data)}
      />
    </>
  );
}

// ─── ProfilesTab ──────────────────────────────────────────────────────────────

function ProfilesTab({
  profiles,
  templates,
  networkEntries,
}: {
  profiles: Profile[];
  templates: Template[];
  networkEntries: NetworkEntry[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<Profile | null>(null);
  const [formKey, setFormKey] = useState(0);

  const save = useMutation({
    mutationFn: async (data: ProfFormData) => {
      const payload: Record<string, unknown> = {
        ...data,
        agent_family: data.agent_family || undefined,
        preferred_patch_mode: data.preferred_patch_mode || undefined,
        context_budget: data.context_budget > 0 ? data.context_budget : undefined,
        timeout_ms: data.timeout_ms > 0 ? data.timeout_ms : undefined,
      };
      // Prázdný api_key při edit = nechat stávající (password-field convention).
      if (!data.api_key) delete payload.api_key;
      // Proxy je strictly per-node — žádný share toggle. Field share_globally
      // odstraněn z formuláře, /share endpoint deprecated.
      const res = await api.mcp<{ ok?: boolean; error?: string }>(
        'llm-profiles.profile_upsert',
        payload,
      );
      if (res.error) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      toast.success(t('llmProfiles.proxySaved'));
      qc.invalidateQueries({ queryKey: ['llm-profiles-data'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.mcp<{ ok?: boolean; error?: string }>(
        'llm-profiles.profile_delete',
        { id },
      );
      if (res.error) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      toast.success(t('llmProfiles.proxyDeleted'));
      qc.invalidateQueries({ queryKey: ['llm-profiles-data'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleEnabled = async (p: Profile, v: boolean) => {
    const res = await api.mcp<{ ok?: boolean; error?: string }>(
      'llm-profiles.profile_upsert',
      { id: p.id, enabled: v },
    );
    if (res.error) throw new Error(res.error);
    qc.invalidateQueries({ queryKey: ['llm-profiles-data'] });
  };

  const togglePublish = useMutation({
    mutationFn: async ({ id, enabled, domain }: {
      id: string;
      enabled: boolean;
      domain: string;
    }) => {
      if (enabled) {
        return await api.mcp<{ ok?: boolean; error?: string }>(
          'llm-profiles.llm_profile_publish',
          { profile_id: id, domain },
        );
      }
      return await api.mcp<{ ok?: boolean; error?: string }>(
        'llm-profiles.llm_profile_unpublish',
        { domain },
      );
    },
    onSuccess: (r: { ok?: boolean; error?: string }) => {
      if (r.error) {
        toast.error(r.error);
        return;
      }
      toast.success(t('llmProfiles.publishStateUpdated'));
      qc.invalidateQueries({ queryKey: ['catalog-network-llm'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const findNetEntry = (profileId: string): NetworkEntry | undefined => {
    const matches = networkEntries.filter(
      (e) => e.value?.service === 'llm-profiles' && e.value?.instance_ref === profileId,
    );
    // Preferuj fungující entry (actual=true) před broken — historicky mohly
    // vzniknout duplikáty (random suggestDomain bug před fixem).
    return matches.find((e) => e.value?.actual) ?? matches[0];
  };

  const suggestDomain = async (p: Profile): Promise<string> => {
    // Deterministický 8-hex hash z profile.id (SHA-256 prefix). Stejný profile
    // → stejná URL napříč všemi publish operacemi → žádné duplikáty v NPM ani
    // catalog. Model name záměrně NENÍ v doméně (Qwen3.6→Qwen4 nezmění URL,
    // klient zná stabilní endpoint).
    const buf = new TextEncoder().encode(p.id);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    const hash = Array.from(new Uint8Array(digest).slice(0, 4))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return `${hash}.llm.services.maxcloud.cz`;
  };

  const handlePublishToggle = async (p: Profile) => {
    const entry = findNetEntry(p.id);
    if (entry?.value?.desired) {
      // už publikováno → unpublish
      if (!(await themedConfirm(t('llmProfiles.confirmUnpublish', { url: entry.value.url })))) return;
      togglePublish.mutate({ id: p.id, enabled: false, domain: entry.value.url });
    } else {
      // publish nový — zeptej se na doménu (default = deterministický hash)
      const suggested = entry?.value?.url || (await suggestDomain(p));
      const domain = await themedPrompt(
        t('llmProfiles.promptPublicDomain'),
        suggested,
      );
      if (!domain || !domain.trim()) return;
      togglePublish.mutate({ id: p.id, enabled: true, domain: domain.trim() });
    }
  };

  const openEdit = (p: Profile) => {
    setEditItem(p);
    setFormKey((k) => k + 1);
    setModalOpen(true);
  };

  const openNew = () => {
    setEditItem(null);
    setFormKey((k) => k + 1);
    setModalOpen(true);
  };

  const tplLabel = (id: string | null) =>
    id ? (templates.find((t) => t.id === id)?.label ?? id) : '—';

  if (!profiles.length) {
    return (
      <EmptyState
        icon={<User size={32} className='text-gray-600' />}
        title={t('llmProfiles.noProxiesTitle')}
        description={t('llmProfiles.noProxiesDesc')}
        action={
          <Button variant='primary' onClick={openNew}>
            <Plus size={14} className='mr-1' />
            {t('llmProfiles.newProxy')}
          </Button>
        }
      />
    );
  }

  return (
    <>
      <div className='flex items-center justify-between mb-4'>
        <span className='text-sm text-gray-500'>
          {t('llmProfiles.proxiesCount', { value: profiles.length })}
        </span>
        <Button variant='primary' onClick={openNew}>
          <Plus size={14} className='mr-1' />
          {t('llmProfiles.newProxy')}
        </Button>
      </div>

      <div className='space-y-2'>
        {profiles.map((p) => {
          const tpl = templates.find((t) => t.id === p.template_id);
          const effectiveBackend = p.backend ?? tpl?.backend ?? 'openai';

          return (
            <Card key={p.id} className='group'>
              <CardContent className='p-0'>
                <div className='flex items-start gap-4 px-4 py-3'>
                  <div className='mt-0.5 flex-shrink-0'>
                    <AsyncStatusToggle
                      value={p.enabled}
                      onToggle={(v) => toggleEnabled(p, v)}
                      onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
                    />
                  </div>

                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <span className='font-medium text-sm text-white'>
                        {p.label ?? tpl?.label ?? p.id}
                      </span>
                      <BackendBadge backend={effectiveBackend} />
                      {p.admin_only && (
                        <Badge variant='red'>
                          <Lock size={10} className='mr-0.5 inline' />admin
                        </Badge>
                      )}
                      {p.node_scope === 'global'
                        ? (
                          <Badge variant='blue'>
                            <Globe size={10} className='mr-0.5 inline' />
                            {t('llmProfiles.platform')}
                          </Badge>
                        )
                        : p.node_scope && (
                          <Badge variant='gray'>
                            <Server size={10} className='mr-0.5 inline' />
                            {p.node_scope}
                          </Badge>
                        )}
                      {!p.enabled && <Badge variant='gray'>{t('llmProfiles.disabled')}</Badge>}
                      {p.priority !== 0 && <Badge variant='gray'>p{p.priority}</Badge>}
                    </div>

                    <div className='text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5'>
                      {p.template_id && (
                        <span className='opacity-70'>
                          <Layers size={10} className='inline mr-0.5' />
                          {tplLabel(p.template_id)}
                        </span>
                      )}
                      {(p.model ?? tpl?.model) && (
                        <span className='font-mono'>{p.model ?? tpl?.model}</span>
                      )}
                      {p.daily_token_limit > 0 && (
                        <span>
                          {t('llmProfiles.tokensPerDay', {
                            value: p.daily_token_limit.toLocaleString(),
                          })}
                        </span>
                      )}
                    </div>

                    {p.api_key && (
                      <div className='mt-1.5 flex items-center gap-2'>
                        <KeyRound size={11} className='text-gray-600' />
                        <span className='text-xs font-mono text-gray-500'>{p.api_key}</span>
                      </div>
                    )}

                    {(() => {
                      const entry = findNetEntry(p.id);
                      if (!entry) return null;
                      const desired = !!entry.value?.desired;
                      const actual = !!entry.value?.actual;
                      const syncing = desired !== actual;
                      const url = entry.value?.url;
                      return (
                        <div className='mt-1.5 flex items-center gap-2 text-xs'>
                          <Globe
                            size={11}
                            className={actual ? 'text-green-500' : 'text-gray-500'}
                          />
                          <a
                            href={`https://${url}/v1/models`}
                            target='_blank'
                            rel='noopener'
                            className='font-mono text-blue-400 hover:underline'
                          >
                            {url}
                          </a>
                          {syncing
                            ? <Badge variant='yellow'>syncing</Badge>
                            : actual
                            ? <Badge variant='green'>public</Badge>
                            : <Badge variant='gray'>off</Badge>}
                          <button
                            type='button'
                            onClick={() => navigator.clipboard.writeText(`https://${url}/v1`)}
                            className='text-gray-500 hover:text-white'
                            title={t('llmProfiles.copyUrl')}
                          >
                            ⧉
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                  <div className='flex items-center gap-3 flex-shrink-0'>
                    <span className='text-xs text-gray-600 hidden sm:block'>
                      {fmtDate(p.created_at)}
                    </span>
                    <Button
                      variant={findNetEntry(p.id)?.value?.desired ? 'success' : 'ghost'}
                      onClick={() => handlePublishToggle(p)}
                      disabled={togglePublish.isPending}
                      title={findNetEntry(p.id)?.value?.desired
                        ? t('llmProfiles.clickToUnpublish')
                        : t('llmProfiles.clickToPublish')}
                    >
                      <Globe size={12} className='mr-1' />
                      {findNetEntry(p.id)?.value?.desired
                        ? t('llmProfiles.public')
                        : t('llmProfiles.publish')}
                    </Button>
                    <InlineActions.Row
                      onEdit={(e) => {
                        e.stopPropagation();
                        openEdit(p);
                      }}
                      onDelete={() => del.mutate(p.id)}
                      confirmDelete={t('llmProfiles.confirmDeleteProxy', { name: p.label ?? p.id })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ProfileForm
        key={formKey}
        open={modalOpen}
        initial={editItem
          ? {
            id: editItem.id,
            template_id: editItem.template_id,
            label: editItem.label,
            url: editItem.url,
            model: editItem.model,
            backend: editItem.backend,
            api_key: '',
            enabled: editItem.enabled,
            priority: editItem.priority,
            daily_token_limit: editItem.daily_token_limit,
            admin_only: editItem.admin_only,
            consumers: Array.isArray((editItem as { consumers?: string[] }).consumers)
              ? (editItem as { consumers: string[] }).consumers
              : [],
            codable_compatible: editItem.codable_compatible === true,
            codable_enabled: editItem.codable_enabled === true,
            agent_family: editItem.agent_family ?? '',
            preferred_patch_mode: editItem.preferred_patch_mode ?? 'full-file',
            context_budget: editItem.context_budget ?? 120000,
            vision: editItem.vision === true,
            timeout_ms: editItem.timeout_ms ?? 120000,
          }
          : null}
        templates={templates}
        onClose={() => setModalOpen(false)}
        onSave={(data) => save.mutateAsync(data)}
      />
    </>
  );
}

// ─── External Keys Tab ────────────────────────────────────────────────────────

function ExternalKeysTab({
  keys,
  profiles,
  entities,
}: {
  keys: ExternalKey[];
  templates: Template[];
  profiles: Profile[];
  entities: EntityChoice[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showSecret, setShowSecret] = useState<{ id: string; key: string; label: string } | null>(
    null,
  );
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    label: '',
    allowed_models: '',
    allowed_proxies: [] as string[],
    rate_limit_rpm: '',
    concurrent_limit: '',
    entity_id: '',
    expires_at: '',
  });
  const entityById = (id: string | null) => id ? entities.find((e) => e.id === id) : undefined;
  const proxyById = (id: string) => profiles.find((p) => p.id === id);

  const create = useMutation({
    mutationFn: async () => {
      const allowedArray = form.allowed_models.trim()
        ? form.allowed_models.split(',').map((s) => s.trim()).filter(Boolean)
        : ['*'];
      const body: Record<string, unknown> = {
        label: form.label.trim(),
        allowed_models: allowedArray,
      };
      if (form.allowed_proxies.length > 0) body.allowed_proxies = form.allowed_proxies;
      if (form.rate_limit_rpm) body.rate_limit_rpm = Number(form.rate_limit_rpm);
      if (form.concurrent_limit) body.concurrent_limit = Number(form.concurrent_limit);
      if (form.entity_id) body.entity_id = form.entity_id;
      if (form.expires_at) body.expires_at = form.expires_at;
      const res = await api.mcp<{ id: string; key: string; label: string; error?: string }>(
        'llm-profiles.llm_external_key_create',
        body,
      );
      return res;
    },
    onSuccess: (r) => {
      if (r.error) {
        toast.error(r.error);
        return;
      }
      setShowSecret({ id: r.id, key: r.key, label: r.label });
      setCreating(false);
      setForm({
        label: '',
        allowed_models: '',
        allowed_proxies: [],
        rate_limit_rpm: '',
        concurrent_limit: '',
        entity_id: '',
        expires_at: '',
      });
      qc.invalidateQueries({ queryKey: ['llm-external-keys'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Nevratné fyzické smazání klíče (MCP hard delete).
  const realDelete = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.mcp<{ ok?: boolean; error?: string }>(
        'llm-profiles.external_key_delete',
        { id },
      );
      if (res.error) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      toast.success(t('llmProfiles.keyDeleted'));
      qc.invalidateQueries({ queryKey: ['llm-external-keys'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Reverzibilní enable/disable toggle.
  const toggleEnabled = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const res = await api.mcp<{ ok?: boolean; error?: string }>(
        'llm-profiles.external_key_set_enabled',
        { id, enabled },
      );
      if (res.error) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['llm-external-keys'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Seznam unikátních model ID z templates + profiles (pro autocomplete v UI)

  return (
    <>
      <div className='flex items-center justify-between mb-4'>
        <span className='text-sm text-gray-500'>
          {t('llmProfiles.keysCount', { value: keys.length })}
          {keys.filter((k) => k.enabled).length !== keys.length &&
            ` ${
              t('llmProfiles.keysActiveSuffix', { value: keys.filter((k) => k.enabled).length })
            }`}
        </span>
        <Button variant='primary' onClick={() => setCreating(true)}>
          <Plus size={14} className='mr-1' />
          {t('llmProfiles.newKey')}
        </Button>
      </div>

      {keys.length === 0
        ? (
          <EmptyState
            icon={<KeyRound size={32} className='text-gray-600' />}
            title={t('llmProfiles.noKeysTitle')}
            description={t('llmProfiles.noKeysDesc')}
          />
        )
        : (
          <div className='space-y-2'>
            {keys.map((k) => (
              <Card key={k.id} className='group'>
                <CardContent className='p-3'>
                  <div className='flex items-start gap-3'>
                    <KeyRound
                      size={14}
                      className={k.enabled ? 'text-green-500 mt-1' : 'text-gray-600 mt-1'}
                    />
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <span className='font-medium text-sm text-white'>{k.label}</span>
                        {!k.enabled && <Badge variant='gray'>revoked</Badge>}
                        {(() => {
                          const ent = entityById(k.entity_id);
                          if (!ent) {
                            return k.entity_id
                              ? (
                                <Badge variant='gray'>
                                  {t('llmProfiles.entityShort', { id: k.entity_id.slice(0, 8) })}
                                </Badge>
                              )
                              : <Badge variant='gray'>{t('llmProfiles.noEntity')}</Badge>;
                          }
                          return <Badge variant='blue'>{ent.label}</Badge>;
                        })()}
                        {k.allowed_proxies && k.allowed_proxies.length > 0
                          ? k.allowed_proxies.map((pid) => {
                            const p = proxyById(pid);
                            return (
                              <Badge key={`px-${pid}`} variant='green'>
                                <Globe size={10} className='mr-0.5 inline' />
                                {p?.label || pid}
                              </Badge>
                            );
                          })
                          : <Badge variant='gray'>{t('llmProfiles.allProxies')}</Badge>}
                        {k.allowed_models.includes('*')
                          ? <Badge variant='blue'>any model</Badge>
                          : k.allowed_models.map((m) => <Badge key={m} variant='gray'>{m}</Badge>)}
                        {k.rate_limit_rpm && <Badge variant='gray'>{k.rate_limit_rpm} RPM</Badge>}
                        {k.concurrent_limit && (
                          <Badge
                            variant={(k.in_flight ?? 0) >= k.concurrent_limit ? 'yellow' : 'blue'}
                          >
                            {t('llmProfiles.slotsUsage', {
                              used: k.in_flight ?? 0,
                              limit: k.concurrent_limit,
                            })}
                          </Badge>
                        )}
                        {k.expires_at && (
                          <Badge variant='yellow'>
                            expires {fmtDate(k.expires_at)}
                          </Badge>
                        )}
                      </div>
                      <div className='text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3'>
                        <span className='font-mono'>{k.key_prefix}…</span>
                        <span>{t('llmProfiles.created', { date: fmtDate(k.created_at) })}</span>
                        {k.issued_by
                          ? (
                            <span>
                              {t('llmProfiles.issuedBy', {
                                label: k.issued_by_label || k.issued_by.slice(0, 8),
                              })}
                            </span>
                          )
                          : (
                            <span className='text-yellow-500'>
                              {t('llmProfiles.originatorMissing')}
                            </span>
                          )}
                        {k.last_used && (
                          <span>
                            {t('llmProfiles.lastUsed', { date: fmtDate(k.last_used) })}
                            {k.last_used_model && ` (${k.last_used_model})`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='flex flex-col gap-1 items-end'>
                      {/* Toggle enable/disable — reverzibilní */}
                      <Button
                        variant={k.enabled ? 'ghost' : 'primary'}
                        onClick={() => toggleEnabled.mutate({ id: k.id, enabled: !k.enabled })}
                        disabled={toggleEnabled.isPending}
                        title={k.enabled
                          ? t('llmProfiles.disableTemporarily')
                          : t('llmProfiles.enableAgain')}
                      >
                        {k.enabled ? t('llmProfiles.disable') : t('llmProfiles.enable')}
                      </Button>
                      {/* Trvalé smazání — nevratné */}
                      <Button
                        variant='danger'
                        onClick={async () => {
                          if (
                            await themedConfirm(t('llmProfiles.confirmDeleteKey', { name: k.label }))
                          ) realDelete.mutate(k.id);
                        }}
                        disabled={realDelete.isPending}
                        title={t('llmProfiles.permanentDelete')}
                      >
                        {t('llmProfiles.delete')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      {/* Create modal */}
      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title={t('llmProfiles.newKeyModalTitle')}
      >
        <div className='space-y-3'>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>
              {t('llmProfiles.keyLabelRequired')}
            </label>
            <input
              type='text'
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={t('llmProfiles.keyLabelPlaceholder')}
              className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>
              {t('llmProfiles.allowedProxiesLabel')}{' '}
              <span className='text-gray-600'>{t('llmProfiles.allowedProxiesHint')}</span>
            </label>
            <div className='space-y-1 max-h-40 overflow-y-auto bg-gray-800 border border-gray-700 rounded p-2'>
              {profiles.length === 0
                ? <span className='text-xs text-gray-500'>{t('llmProfiles.noProxiesDefined')}</span>
                : profiles.map((p) => (
                  <label
                    key={p.id}
                    className='flex items-center gap-2 text-sm text-gray-300 cursor-pointer'
                  >
                    <input
                      type='checkbox'
                      className='w-4 h-4 accent-blue-500'
                      checked={form.allowed_proxies.includes(p.id)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...form.allowed_proxies, p.id]
                          : form.allowed_proxies.filter((id) => id !== p.id);
                        setForm({ ...form, allowed_proxies: next });
                      }}
                    />
                    <span className='font-mono text-xs'>{p.id}</span>
                    {p.label && <span className='text-xs text-gray-500'>— {p.label}</span>}
                  </label>
                ))}
            </div>
            <p className='mt-1 text-xs text-gray-600'>
              {t('llmProfiles.allowedProxiesNote')}
            </p>
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>
              {t('llmProfiles.allowedModelsLabel')}{' '}
              <span className='text-gray-600'>{t('llmProfiles.allowedModelsHint')}</span>
            </label>
            <input
              type='text'
              value={form.allowed_models}
              onChange={(e) => setForm({ ...form, allowed_models: e.target.value })}
              placeholder={t('llmProfiles.allowedModelsPlaceholder')}
              className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>
              {t('llmProfiles.ownerEntityLabel')}{' '}
              <span className='text-gray-600'>{t('llmProfiles.ownerEntityHint')}</span>
            </label>
            <Select
              value={form.entity_id}
              onChange={(e) => setForm({ ...form, entity_id: e.target.value })}
              className='w-full'
            >
              <option value=''>{t('llmProfiles.currentEntityOption')}</option>
              {entities.map((e) => (
                <option key={e.id} value={e.id}>
                  [{e.type}] {e.label}
                </option>
              ))}
            </Select>
          </div>
          <div className='grid grid-cols-3 gap-3'>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>
                {t('llmProfiles.rateLimitRpm')}
              </label>
              <input
                type='number'
                value={form.rate_limit_rpm}
                onChange={(e) => setForm({ ...form, rate_limit_rpm: e.target.value })}
                placeholder={t('llmProfiles.unlimited')}
                className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
              />
            </div>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>
                {t('llmProfiles.concurrentSlots')}
              </label>
              <input
                type='number'
                min='1'
                value={form.concurrent_limit}
                onChange={(e) => setForm({ ...form, concurrent_limit: e.target.value })}
                placeholder={t('llmProfiles.unlimited')}
                className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
              />
            </div>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>
                {t('llmProfiles.expirationOptional')}
              </label>
              <input
                type='datetime-local'
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                className='w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white'
              />
            </div>
          </div>
          <p className='text-xs text-gray-600'>
            <strong>{t('llmProfiles.concurrentSlots')}</strong>
            {t('llmProfiles.slotsNote')}
          </p>
          <div className='flex justify-end gap-2 mt-4'>
            <Button variant='ghost' onClick={() => setCreating(false)}>
              {t('llmProfiles.cancel')}
            </Button>
            <Button
              variant='primary'
              onClick={() => create.mutate()}
              disabled={!form.label.trim() || create.isPending}
            >
              {t('llmProfiles.create')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Show-once secret modal */}
      <Modal
        open={!!showSecret}
        onClose={() => setShowSecret(null)}
        title={t('llmProfiles.keyCreatedTitle')}
      >
        {showSecret && (
          <div className='space-y-3'>
            <p className='text-sm text-yellow-400 bg-yellow-900/20 border border-yellow-700/40 rounded p-3'>
              {t('llmProfiles.keyCreatedWarning')}
            </p>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>
                {t('llmProfiles.labelField')}
              </label>
              <div className='font-medium text-white'>{showSecret.label}</div>
            </div>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>
                {t('llmProfiles.apiKeyField')}
              </label>
              <div className='flex items-center gap-2'>
                <code className='flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm font-mono text-white break-all'>
                  {showSecret.key}
                </code>
                <Button
                  variant='secondary'
                  onClick={() => {
                    navigator.clipboard.writeText(showSecret.key);
                    toast.success(t('llmProfiles.copied'));
                  }}
                >
                  {t('llmProfiles.copy')}
                </Button>
              </div>
            </div>
            <div className='text-xs text-gray-500'>
              {t('llmProfiles.usage')}{' '}
              <code>Authorization: Bearer {showSecret.key.slice(0, 16)}…</code>
            </div>
            <div className='flex justify-end mt-4'>
              <Button variant='primary' onClick={() => setShowSecret(null)}>
                {t('llmProfiles.done')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LlmProfilesPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'templates' | 'profiles' | 'keys'>('templates');

  const { data: tplData } = useQuery({
    queryKey: ['llm-templates'],
    queryFn: () =>
      api.mcp<{ templates: Template[]; total: number }>('llm-profiles.llm_templates_list', {
        include_admin: true,
      }),
    refetchInterval: 30_000,
  });

  const { data: profData } = useQuery({
    queryKey: ['llm-profiles-data'],
    queryFn: () =>
      api.mcp<{ profiles: Profile[]; total: number }>('llm-profiles.list', {
        include_admin: true,
      }),
    refetchInterval: 30_000,
  });

  // Catalog network entries — pro per-profile publish state (desired/actual)
  const { data: netData } = useQuery({
    queryKey: ['catalog-network-llm'],
    queryFn: () =>
      api.mcp<{ ok: boolean; entries: NetworkEntry[] }>('catalog.list', {
        registry_id: 'network',
        limit: 200,
      }),
    refetchInterval: 5_000, // syncing → potřebujeme rychlejší refresh
  });

  // External API keys
  const { data: keysData } = useQuery({
    queryKey: ['llm-external-keys'],
    queryFn: () =>
      api.mcp<{ keys: ExternalKey[]; total: number }>('llm-profiles.llm_external_key_list', {}),
    refetchInterval: 5_000, // 5s — chceme live in_flight counter pro slot monitoring
  });

  // Entity list pro dropdown při key assignment — identity.entity_list (admin).
  // Non-admin dostane scope error → fallback na prázdné pole.
  const { data: entData } = useQuery({
    queryKey: ['llm-entities'],
    queryFn: () =>
      api
        .mcp<{ ok?: boolean; entities: EntityChoice[] }>('identity.entity_list', {})
        .then((r) => ({
          entities: (r.entities ?? []).filter(
            (e) => (e as EntityChoice & { enabled?: boolean }).enabled !== false,
          ),
        }))
        .catch(() => ({ entities: [] as EntityChoice[] })),
    refetchInterval: 60_000,
  });

  const templates: Template[] = tplData?.templates ?? [];
  const profiles: Profile[] = profData?.profiles ?? [];
  const allNetEntries: NetworkEntry[] = netData?.entries ?? [];
  const networkEntries = allNetEntries.filter((e) => e.value?.service === 'llm-profiles');
  const keys: ExternalKey[] = keysData?.keys ?? [];
  const entities: EntityChoice[] = entData?.entities ?? [];

  const activProf = profiles.filter((p) => p.enabled);
  const publishedCount = networkEntries.filter((e) => e.value?.actual).length;
  const activeKeys = keys.filter((k) => k.enabled).length;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='LLM Profiles'
        description={t('llmProfiles.pageDescription')}
        icon={<Cpu size={20} />}
      />

      <StatsGrid columns={4}>
        <StatCard label={t('llmProfiles.statModels')} value={templates.length} icon={Layers} />
        <StatCard
          label={t('llmProfiles.statProxies')}
          value={`${activProf.length}/${profiles.length}`}
          icon={Server}
        />
        <StatCard
          label={t('llmProfiles.statPublished')}
          value={publishedCount}
          icon={Globe}
          variant='info'
        />
        <StatCard
          label={t('llmProfiles.statExternalKeys')}
          value={activeKeys}
          icon={KeyRound}
          variant='success'
        />
      </StatsGrid>

      <Card>
        <CardContent className='pt-5'>
          <SimpleTabs
            tabs={[
              { id: 'templates', label: t('llmProfiles.tabModels', { value: templates.length }) },
              { id: 'profiles', label: t('llmProfiles.tabProxies', { value: profiles.length }) },
              { id: 'keys', label: t('llmProfiles.tabExternalKeys', { value: keys.length }) },
            ]}
            active={tab}
            onSwitch={(id) => setTab(id as typeof tab)}
          />

          {tab === 'templates' && <TemplatesTab templates={templates} />}
          {tab === 'profiles' && (
            <ProfilesTab
              templates={templates}
              profiles={profiles}
              networkEntries={networkEntries}
            />
          )}
          {tab === 'keys' && (
            <ExternalKeysTab
              keys={keys}
              templates={templates}
              profiles={profiles}
              entities={entities}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
